import { genderList } from '@/src/constants/visa/visaRequirements'
import {
    Calendar,
    Container,
    Dropdown,
    Spacer,
    TextField,
    Typography
} from '@/src/ct-design/lib'
import { TravellerDetails } from '@/src/types/visa/visaItinerary'
import { useState } from 'react'
import CountryCodeBlock from '@/src/container/common/travellerInfo/countryCode'
import CountryCodeList from '@/src/container/common/travellerInfo/countryCode/countryCodeList'
import { countryDataType } from '@/src/types'
import useVisaRequirements from '@/src/hooks/useVisaRequirements'
import { DESKTOP } from '@/src/constants'

const PersonalDetails = ({
    selectedTraveller,
    onFieldChange,
    errorFields,
    contactDetails,
    updateMobileValidation
}: {
    selectedTraveller: TravellerDetails
    onFieldChange: (field: string, value: string) => void
    errorFields: Record<string, string>
    contactDetails: {
        isdCode: string
        phoneNumber: string
        customerEmail: string
    }
    updateMobileValidation: (countryData: countryDataType) => void
}) => {
    const [showList, setShowList] = useState(false)
    const { isPrimary, passportDetails, personalDetails } = selectedTraveller
    const { isdCode, phoneNumber, customerEmail } = contactDetails
    const { firstName, lastName, dateOfBirth, gender } = personalDetails
    const { passportNumber, expiryDate } = passportDetails
    const { dateOfArrival } = useVisaRequirements(DESKTOP)
    const onCountryItemClick = (key: string, countryData: countryDataType) => {
        setShowList(false)
        //dispatch action to update the country code
        updateMobileValidation({ ...countryData, name: key })
        onFieldChange('isdCode', countryData.code)
        onFieldChange('phoneNumber', '')
    }

    const defaultSingleSelectedIndex = genderList.findIndex(
        ({ item }) => item === gender
    )
    return (
        <>
            <Container className='flex flex-row flex-between'>
                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>First / Given Name</Typography>
                    <Spacer className='mb-2' />
                    <TextField
                        value={firstName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onFieldChange('firstName', e.target.value.trim())
                        }
                        height='40px'
                        error={errorFields.firstName}
                    />
                </Container>

                <Spacer className='mr-8' />

                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>Last Name</Typography>
                    <Spacer className='mb-2' />
                    <TextField
                        value={lastName}
                        size='sm'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onFieldChange('lastName', e.target.value.trim())
                        }
                        height='40px'
                        error={errorFields.lastName}
                    />
                </Container>
            </Container>
            <Spacer className='mb-5' />
            {isPrimary && (
                <>
                    <Container className='flex flex-row flex-between'>
                        <Container className='flex flex-column flex-1'>
                            <Typography variant='B2'>Email</Typography>
                            <Spacer className='mb-2' />
                            <TextField
                                value={customerEmail}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                    onFieldChange(
                                        'customerEmail',
                                        e.target.value.trim()
                                    )
                                }
                                height='40px'
                                // disabled={true}
                                error={errorFields.customerEmail}
                            />
                        </Container>

                        <Spacer className='mr-8' />

                        <Container className='flex flex-column flex-1 p-relative'>
                            <Typography variant='B2'>Phone Number</Typography>
                            <Spacer className='mb-2' />
                            <TextField
                                value={phoneNumber}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) =>
                                    onFieldChange('phoneNumber', e.target.value)
                                }
                                height='40px'
                                prefix={
                                    <CountryCodeBlock
                                        isdCode={isdCode}
                                        onClick={() => setShowList(!showList)}
                                    />
                                }
                                error={errorFields.phoneNumber}
                            />
                            {showList && (
                                <CountryCodeList
                                    onClick={onCountryItemClick}
                                    hideList={() => setShowList(!showList)}
                                />
                            )}
                        </Container>
                    </Container>
                    <Spacer className='mb-5' />
                </>
            )}

            <Container className='flex flex-row flex-between'>
                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>Date of Birth</Typography>
                    <Spacer className='mb-2' />
                    <Calendar
                        selectedDate={
                            dateOfBirth ? new Date(dateOfBirth) : null
                        }
                        type='dateOfBirth'
                        onDateChange={(_, date) => {
                            onFieldChange('dateOfBirth', date.toISOString())
                        }}
                        error={errorFields.dateOfBirth}
                        startYear={new Date().getFullYear()}
                        endYear={1900}
                        yearOrderDesc={true}
                        disableDate={dateOfArrival}
                    />
                </Container>

                <Spacer className='mr-8' />

                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>Gender</Typography>
                    <Spacer className='mb-2' />
                    <Dropdown
                        dropDownValue={gender}
                        placeHolder='Select gender'
                        itemList={genderList}
                        onItemSelect={({ item }) => {
                            onFieldChange('gender', item)
                        }}
                        defaultSingleSelectedIndex={defaultSingleSelectedIndex}
                        hasError={!!errorFields.gender}
                    />
                    {errorFields.gender && (
                        <Typography
                            variant='P3'
                            color='warning'
                            className='mt-2 ml-4'
                        >
                            {errorFields.gender}
                        </Typography>
                    )}
                </Container>
            </Container>
            <Spacer className='mb-5' />
            <Container className='flex flex-row flex-between'>
                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>Passport Number</Typography>
                    <Spacer className='mb-2' />
                    <TextField
                        value={passportNumber}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            onFieldChange('passportNumber', e.target.value.trim())
                        }
                        height='40px'
                        error={errorFields.passportNumber}
                    />
                </Container>

                <Spacer className='mr-8' />

                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>Passport Expiry Date</Typography>
                    <Spacer className='mb-2' />
                    <Calendar
                        initialDate={
                            dateOfArrival ? new Date(dateOfArrival) : new Date()
                        }
                        selectedDate={expiryDate ? new Date(expiryDate) : null}
                        type='expiryDate'
                        onDateChange={(_, date) => {
                            onFieldChange('expiryDate', date.toISOString())
                        }}
                        error={errorFields.expiryDate}
                        startYear={new Date().getFullYear()}
                        endYear={new Date().getFullYear() + 20}
                        yearOrderDesc={false}
                        disableDate={dateOfArrival}
                    />
                </Container>
            </Container>
        </>
    )
}

export default PersonalDetails
